import { useDynamicForm } from "@/components/dynamic-form/hook/useDynamicForm";
import { useImmediateFetch } from "@/components/dynamic-form/hook/useImmediateFetch";
import { useWatchPagination } from "@/components/dynamic-form/hook/useWatchPagination";
import { useColumns } from "@/pages/previous-consumption/hooks/useColumns";
import { PageContainer } from "@/components/page-container";
import { TableContainer } from "@/components/table-container";
import { DynamicForm } from "@/components/dynamic-form";
import { useSearchConfig } from "@/pages/previous-consumption/hooks/useSearchConfig";
import { Table } from "@renderbus/ui";
import { getInitialValues } from "@/components/dynamic-form/helper/getInitialValues";
import {
  OperationArea,
  SearchAreaWrapper,
} from "../consumption/components/atom";
import {
  OperationButton,
  OperationButtonType,
} from "@/components/operation-button";
import { useTranslation } from "react-i18next";
import { getToday } from "@/utils/date-formatter/getToday";
import dayjs from "project-config/utils/dayjs";
import { RenderbusTag } from "@/components/renderbus-tag";
import { timeFormatter } from "@/utils/timeFormatter";
import { useGlobalStore } from "@/store/GlobalStore";
import { useConsumeTip } from "@/pages/previous-consumption/hooks/useConsumeTip";
import routerUtil from "@/utils/routerUtil";
import { Modal } from "@/components/renderbus-modal";
import { ExportForm } from "@/pages/previous-consumption/components/export-form";
import { useExportList } from "@/pages/previous-consumption/hooks/useExportList";
import { useState } from "react";
import { formatTime } from "@/components/dynamic-form/formatter/enhanceEndTime";

export function PreviousConsumption() {
  const { t } = useTranslation("center");
  const { userInfo } = useGlobalStore();

  const { consumeData, fetchConsumeData } = useConsumeTip();

  const {
    form,
    tableData,
    fetchData,
    pageNum,
    setPageNum,
    total,
    pageSize,
    getSearchParams,
  } = useDynamicForm({
    api: "/api/rendering/task/renderingTask/findConsumptionList",
    defaultPageSize: 10,
    formatter: {
      time: formatTime,
    },
  });

  const fetch = async () => {
    fetchData().then();
    fetchConsumeData(getSearchParams()).then();
  };

  useImmediateFetch(fetch);
  useWatchPagination(pageSize, pageNum, fetch);

  const { searchConfig } = useSearchConfig();

  const { columns } = useColumns();

  const updateData = (days: number) => {
    const today = getToday();
    form.setFieldsValue({
      time: [
        dayjs(new Date(today.getTime() - days * 24 * 3600 * 1000)),
        dayjs(today),
      ],
    });

    fetch().then();
  };

  const [currentActiceName, setCurrentActiveName] = useState(
    t("user_consume_recent_week")
  );

  const {
    exportType,
    setExportType,
    onExport,
    openExportModal,
    setOpenExportModal,
  } = useExportList(getSearchParams({ hiddenPageSize: true }));

  const symbol = userInfo?.zone === 1 ? "CNY" : "USD";
  const symbol2 = userInfo?.zone === 1 ? "￥" : "$";
  const symbol3 = userInfo?.zone === 1 ? "CCNY" : "CUSD";

  return (
    <>
      <PageContainer>
        <TableContainer>
          <RenderbusTag>
            {t("consumption_previous_consumption_warning")}{" "}
            <OperationButton
              text={t("consumption_check_new_consumption")}
              onClick={() => routerUtil.navigate?.("/consumption")}
            />
          </RenderbusTag>

          <SearchAreaWrapper>
            <DynamicForm
              initialValues={getInitialValues(searchConfig)}
              searchConfig={searchConfig}
              searchForm={form}
              onSearch={fetch}
            />

            <OperationArea>
              <OperationButton
                onClick={async () => {
                  userInfo?.exportFrameConsume
                    ? setOpenExportModal(true)
                    : onExport().then();
                }}
                text={t("user_consume_pick_output")}
              />
              <OperationButton
                type={
                  currentActiceName === t("user_consume_recent_week")
                    ? OperationButtonType.Normal
                    : OperationButtonType.Disabled
                }
                onClick={() => {
                  setCurrentActiveName(t("user_consume_recent_week"));
                  updateData(7);
                }}
                text={t("user_consume_recent_week")}
              />
              <OperationButton
                type={
                  currentActiceName === t("user_consume_recent_month")
                    ? OperationButtonType.Normal
                    : OperationButtonType.Disabled
                }
                onClick={() => {
                  setCurrentActiveName(t("user_consume_recent_month"));
                  updateData(30);
                }}
                text={t("user_consume_recent_month")}
              />
              <OperationButton
                type={
                  currentActiceName === t("user_consume_recent_quarter")
                    ? OperationButtonType.Normal
                    : OperationButtonType.Disabled
                }
                onClick={() => {
                  setCurrentActiveName(t("user_consume_recent_quarter"));
                  updateData(90);
                }}
                text={t("user_consume_recent_quarter")}
              />
            </OperationArea>
          </SearchAreaWrapper>

          <RenderbusTag>
            <span>
              {t("user_consume_info_time")}
              <label>{timeFormatter(consumeData?.consumeTime ?? 0)}</label>
            </span>{" "}
            <span>
              {t("user_consume_info_account", { 0: symbol })}
              <label>
                {t("amount_unit_with_money", {
                  0: symbol2,
                  1: consumeData?.userAccountConsume ?? 0,
                })}
              </label>
            </span>{" "}
            <span>
              {t("user_consume_info_coupon", { 0: symbol3 })}
              <label>
                {t("amount_unit_with_money", {
                  0: symbol2,
                  1: consumeData?.couponConsume ?? 0,
                })}
              </label>
            </span>{" "}
            {userInfo?.zone === 1 && (
              <span>
                {t("user_consume_info_qycoupon", { 0: symbol3 })}

                <label>
                  {t("amount_unit_with_money", {
                    0: symbol2,
                    1: consumeData?.qyCouponConsume ?? 0,
                  })}
                </label>
              </span>
            )}
          </RenderbusTag>

          <Table
            paginationCurrent={pageNum}
            columns={columns}
            dataSource={tableData}
            gap={false}
            total={total}
            defaultPageSize={pageSize}
            onPageNumChange={async (num) => {
              setPageNum(num);
            }}
          />
        </TableContainer>
      </PageContainer>

      <Modal
        open={openExportModal}
        title={t("user_consume_pick_output_dialog_title")}
        onOk={() => {
          onExport().then(() => setOpenExportModal(false));
        }}
        onCancel={() => setOpenExportModal(false)}
      >
        <ExportForm
          value={exportType}
          onChange={(v) => setExportType(v.target.value)}
        />
      </Modal>
    </>
  );
}
